import { useState, useCallback, useRef, useEffect } from "react";

type UseControlledStateOptions<T> = {
  value?: T;
  defaultValue?: T;
  onChange?: (value: T) => void;
};
export function useControlledState<T>(
  options: UseControlledStateOptions<T> & { value: T }
): [T, (newValue: T) => void];

export function useControlledState<T>(
  options: UseControlledStateOptions<T> & { defaultValue: T }
): [T, (newValue: T) => void];

export function useControlledState<T>(
  options: UseControlledStateOptions<T>
): [T | undefined, (newValue: T) => void];

export function useControlledState<T>({
  defaultValue,
  onChange,
  value: controlledValue,
}: UseControlledStateOptions<T>): [T | undefined, (newValue: T) => void] {
  const isControlled = controlledValue !== undefined;
  const isControlledRef = useRef(isControlled);

  useEffect(() => {
    if (
      process.env.NODE_ENV === "production" ||
      isControlledRef.current === isControlled
    ) {
      return;
    }

    console.warn(
      [
        "Component is changing from",
        isControlledRef.current ? "controlled" : "uncontrolled",
        "to",
        isControlled ? "controlled." : "uncontrolled.",
        "This is an anti-pattern and may cause unexpected behavior.",
      ].join(" ")
    );
  }, [isControlled]);

  const [internalValue, setInternalValue] = useState<T | undefined>(
    defaultValue
  );

  const value = isControlled ? controlledValue : internalValue;

  const setValue = useCallback(
    (newValue: T) => {
      onChange?.(newValue);

      // Update internal state only in uncontrolled mode
      if (!isControlled) {
        setInternalValue(newValue);
      }
    },
    [isControlled, onChange]
  );

  return [value, setValue];
}
